import { useState, useEffect, useEffectEvent, useMemo, useRef, memo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from './Modal';
import { Button } from './Button';
import { CommunicationComposer, type ReplyTarget } from './CommunicationComposer';
import { Badge } from './Badge';
import { ContactForm, type ContactFormData } from './ContactForm';
import { Phone, Mail as MailIcon, MailOpen, User, Send, Clock, Trash2, MapPin, Pencil, X, UserPlus, ExternalLink, Music, RefreshCw, Check, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ArrowDownLeft, ArrowUpRight, ThumbsUp, Star, Calendar, Beer, Guitar, UtensilsCrossed, Hotel, Building2, Tent, Users, Landmark, HelpCircle, PartyPopper, Trophy, GraduationCap, Briefcase, AlertTriangle, Flag, ListPlus, Search } from 'lucide-react';
import type { Contact, Communication, ThreadMessage, Venue, EmailTemplate, Band, InterestLevel, BandInterest, MonthPeriod, Month, MailingList, RecontactRequest } from '../../types';
import { format } from 'date-fns';
import { contactsAPI, venuesAPI, emailTemplatesAPI, gmailAPI, mailingListsAPI } from '../../api/endpoints';
import { FileText } from 'lucide-react';
import { InterestModal } from './InterestModal';
import { VenueFieldEditor } from './VenueFieldEditor';
import { getInterestConfig, PAUSE_LEVEL, type VenueFormData } from './venueConstants';
import { MailingListPickerModal } from './MailingListPickerModal';
import { VenueTodos } from './VenueTodos';
import { VenueMiniMap } from './VenueMiniMap';
import clsx from 'clsx';
import { formatAddress } from '../../utils/addressFormat';
import { geocodeVenueAddress } from '../../utils/banGeocode';
import { isFranceCountry } from '../../utils/countries';
import { isTypingTarget } from '../../utils/keyboard';
import { parseAddress, parseAddressList } from '../../utils/emailAddress';
import { LAST_CONTACT_SOURCE_NOTE, countPoolDirections, lastContactPool } from '../../utils/lastContact';
import { effectiveFlag, withVenueFlags } from '../../utils/contactRole';
import { getSchedulePresets } from '../../utils/schedulePresets';
import { patchCachedVenueFields, patchCachedVenueFlags } from '../../utils/venueFlags';
import { isPaused, recontactDate } from '../../utils/venuePause';
import { googleSearchUrl, venueClosedQuery, venueGeneralQuery, venueSearchQuery } from '../../utils/venueWebSearch';
import { externalUrl } from '../../utils/externalUrl';
import { recencyColor, recencyLabel } from '../../utils/recencyColor';
import { Tooltip } from './Tooltip';
import { mailBodyPreview, renderMailBody, type MailBodySource } from '../../utils/mailBody';
import { useAuth } from '../../contexts/AuthContext';
import { useInvalidateTodos } from '../../hooks/useTodos';
import toast from 'react-hot-toast';

/**
 * Number of coordinate-backfill attempts made per venue id this session. Module-level so that
 * navigating prev/next through venues — or reopening the overlay — never refires BAN once a
 * venue has used up its attempts, whether it came back ambiguous or the call itself failed
 * (BAN timeout, transient network error) — the two are indistinguishable here, so a failed
 * attempt is retried automatically with backoff (GEOCODE_RETRY_DELAYS_MS) instead of only on
 * the next manual open.
 */
const geocodeAttemptedCount = new Map<string, number>();
const GEOCODE_MAX_ATTEMPTS = 3;
const GEOCODE_RETRY_DELAYS_MS = [1500, 4000]; // wait before the 2nd and 3rd attempts

// One toast for the venue flag, updated in place however fast it is toggled
const FLAG_TOAST_ID = 'venue-flag';

/**
 * Venue ids already marked seen this session (docs/VENUE_SEEN.md). Module-level, so paging
 * prev/next back over a venue — or reopening it — sends the request once, whatever the `venue`
 * prop the caller still holds says: a caller that keeps the venue in its own state, rather than
 * reading it out of the patched query cache, would otherwise hand the overlay `seen: false` again
 * on every open until it refetches.
 */
const markedSeen = new Set<string>();

/**
 * Icon-only link to a Google results page, with the query spelled out in its tooltip. A link
 * rather than a button so middle- and ctrl-click behave like any other link. Renders nothing
 * for an empty query — that is how the query builders say "not applicable to this venue"
 * (see utils/venueWebSearch.ts).
 */
const GoogleSearchLink = ({ query, icon: Icon, className }: {
  query: string;
  icon: React.ComponentType<{ className?: string }>;
  className?: string;
}) => {
  if (!query) return null;
  return (
    <Tooltip content={<>Google: {query}</>} className="flex items-center">
      <a
        href={googleSearchUrl(query)}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Search Google for: ${query}`}
        className={clsx(
          'flex items-center rounded-sm p-1 text-dark-text-muted transition-colors hover:text-primary-400',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <Icon className="w-3.5 h-3.5" />
      </a>
    </Tooltip>
  );
};

const SaxophoneIcon = ({ size = 24, className }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M14 2l-4 4 8 8 4-4z" />
    <path d="M10 6L4 12c-2 2-2 5 0 7s5 2 7 0l6-6" />
    <circle cx="8" cy="16" r="1" />
    <circle cx="6" cy="14" r="1" />
    <circle cx="10" cy="18" r="1" />
  </svg>
);

const VENUE_TYPE_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  concert_hall: Music,
  bar: Beer,
  concert_bar: Guitar,
  jazz_club: SaxophoneIcon,
  restaurant: UtensilsCrossed,
  hotel: Hotel,
  city_concert_hall: Building2,
  festival: Tent,
  association: Users,
  city: Landmark,
  comite_des_fetes: PartyPopper,
  competition: Trophy,
  mentoring: GraduationCap,
  booking_agency: Briefcase,
  other: HelpCircle,
};

const MONTH_LABELS: Record<string, string> = {
  january: 'Jan', february: 'Feb', march: 'Mar', april: 'Apr',
  may: 'May', june: 'Jun', july: 'Jul', august: 'Aug',
  september: 'Sep', october: 'Oct', november: 'Nov', december: 'Dec',
};

const MONTH_ORDER: Month[] = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

const formatMonthPeriod = (period: MonthPeriod | undefined): string => {
  if (!period) return '-';
  if (period.all_year) return 'All Year';
  if (period.months.length === 0) return '-';
  const indices = period.months.map(m => MONTH_ORDER.indexOf(m)).filter(i => i !== -1).sort((a, b) => a - b);
  if (indices.length === 1) return MONTH_LABELS[MONTH_ORDER[indices[0]]];
  const indexSet = new Set(indices);
  let startIdx = -1;
  for (const idx of indices) {
    if (!indexSet.has((idx - 1 + 12) % 12)) { startIdx = idx; break; }
  }
  const groups: number[][] = [];
  let current: number[] = [];
  let walk = startIdx;
  const visited = new Set<number>();
  for (let i = 0; i < indices.length; i++) {
    if (!indexSet.has(walk)) {
      if (current.length > 0) { groups.push(current); current = []; }
      for (let j = 1; j <= 12; j++) {
        const next = (walk + j) % 12;
        if (indexSet.has(next) && !visited.has(next)) { walk = next; break; }
      }
    }
    current.push(walk);
    visited.add(walk);
    walk = (walk + 1) % 12;
  }
  if (current.length > 0) groups.push(current);
  return groups.map(g => g.length === 1 ? MONTH_LABELS[MONTH_ORDER[g[0]]] : `${MONTH_LABELS[MONTH_ORDER[g[0]]]} to ${MONTH_LABELS[MONTH_ORDER[g[g.length - 1]]]}`).join(', ');
};

/**
 * One month-period line on the venue card (Contact / Answer by / Concert). Rendered whether or
 * not the period is set: an unset line reads "Not set" and is still the handle for the
 * right-click editor, so a venue missing its dates no longer hides the only place to add them.
 */
const MonthPeriodLine = ({ label, period, rowProps }: {
  label: string;
  period: MonthPeriod | undefined;
  rowProps: React.HTMLAttributes<HTMLDivElement>;
}) => {
  const text = formatMonthPeriod(period);
  const isSet = text !== '-';
  return (
    <div {...rowProps}>
      <Calendar className="w-3.5 h-3.5 text-dark-text-muted flex-shrink-0" />
      <span className="text-sm text-dark-text-muted">{label}:</span>
      <span className={clsx('text-sm', isSet ? 'text-dark-text-secondary' : 'text-dark-text-muted italic')}>
        {isSet ? text : 'Not set'}
      </span>
    </div>
  );
};

/**
 * A message body inside a card: the full, sanitized document once the card is expanded, and a
 * plain-text preview of its head while it is clamped to two lines. Parsing a stored body is by
 * far the most expensive thing the overlay does, so it is kept off the collapsed path entirely
 * and cached once it has been done (see utils/mailBody.ts).
 */
function MailBody({ expanded, ...source }: MailBodySource & { expanded: boolean }) {
  if (!expanded) {
    return <div className="email-html-content">{mailBodyPreview(source)}</div>;
  }
  const body = renderMailBody(source);
  return body.html !== null
    ? <div className="email-html-content" dangerouslySetInnerHTML={{ __html: body.html }} />
    : <>{body.text}</>;
}


type CommEntry = { contact: Contact; comm: Communication; index: number; threadMessage?: ThreadMessage };

// Whether the entry's contact is who its thread message was exchanged with: the sender of
// received mail, a To: recipient of sent mail. Same rule as the Inbox's row attribution.
function isExchangedWith({ contact, threadMessage: msg }: CommEntry): boolean {
  const address = contact.email?.trim().toLowerCase();
  if (!msg || !address) return false;
  const parties = msg.direction === 'received' ? parseAddressList(msg.from_address).slice(0, 1) : parseAddressList(msg.to_address);
  return parties.some(p => p.email === address);
}

/** What the header's Sent / Received counts filter the list down to. */
type CommDirectionFilter = 'all' | 'sent' | 'received';

// Which way an entry went — the rule behind its Sent / Received badge and behind the direction
// filter. A scheduled, failed or planned email has not gone out, so it counts towards neither
// header number (`countCommDirections` in utils/lastContact), but it is outbound all the same and
// the Sent filter keeps it on screen: the one place the list runs longer than the count above it.
function entryDirection({ comm, threadMessage }: CommEntry): 'sent' | 'received' {
  if (threadMessage) return threadMessage.direction;
  switch (comm.type) {
    case 'email':
      return comm.email?.status === 'received' ? 'received' : 'sent';
    case 'form':
      return 'sent';
    case 'phone':
      return comm.phone?.direction ?? 'received';
    case 'other':
      return comm.other?.direction ?? 'received';
    case 'thread':
      // A thread listed without its messages (they are flattened out into one entry each).
      return comm.thread?.last_direction ?? 'received';
  }
}

function getLatestCommDate(contact: Contact): string {
  let latest = '';
  contact.communications?.forEach(comm => {
    const date = comm.email?.date_sent || comm.phone?.date || comm.form?.date || comm.other?.date || comm.thread?.last_message_date || '';
    if (date && (!latest || new Date(date) > new Date(latest))) latest = date;
  });
  return latest;
}

function selectBestContact(contacts: Contact[]): Contact {
  const pool = contacts.filter(c => c.flags !== 'gatekeeper');
  const candidates = pool.length > 0 ? pool : contacts;
  return candidates.reduce((best, c) => {
    const bestDate = getLatestCommDate(best);
    const cDate = getLatestCommDate(c);
    if (!bestDate) return cDate ? c : best;
    if (!cDate) return best;
    return new Date(cDate) > new Date(bestDate) ? c : best;
  });
}

/** Return the best available display label for a contact: name > email > phone > fallback. */
function contactDisplayName(c: Contact | null | undefined): string {
  if (!c) return 'Unknown';
  return c.name || c.email || c.phone || 'Unknown';
}

// The other recipients on a thread message, excluding the current user and contact.
// Returns the To and Cc segments separately (empty arrays when none).
function otherRecipients(
  msg: ThreadMessage,
  excludeEmails: (string | undefined | null)[],
): { to: string[]; cc: string[] } {
  const exclude = new Set(excludeEmails.filter(Boolean).map((e) => e!.toLowerCase()));
  const seen = new Set<string>(exclude);
  const pick = (raw: string | undefined) =>
    parseAddressList(raw)
      .filter((a) => {
        if (seen.has(a.email)) return false;
        seen.add(a.email);
        return true;
      })
      .map((a) => a.name || a.email);
  return { to: pick(msg.to_address), cc: pick(msg.cc_address) };
}

// Loose RFC-5322-ish address: enough to pick a mail address out of rendered prose or headers
// without matching bare domains or "@handle" mentions.
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

/**
 * The email address under a screen point, or null when the point is not on one.
 * Resolves the caret position at (x, y) to a text node + offset (caretPositionFromPoint,
 * falling back to WebKit's caretRangeFromPoint) and returns the EMAIL_RE match that spans that
 * offset, so right-clicking anywhere inside the address works and clicking next to it does not.
 */
function emailAtPoint(x: number, y: number): string | null {
  let node: Node | null = null;
  let offset = 0;
  if (typeof document.caretPositionFromPoint === 'function') {
    const pos = document.caretPositionFromPoint(x, y);
    if (!pos) return null;
    node = pos.offsetNode;
    offset = pos.offset;
  } else if (typeof document.caretRangeFromPoint === 'function') {
    const range = document.caretRangeFromPoint(x, y);
    if (!range) return null;
    node = range.startContainer;
    offset = range.startOffset;
  }
  if (!node || node.nodeType !== Node.TEXT_NODE) return null;
  for (const m of (node.textContent ?? '').matchAll(EMAIL_RE)) {
    const start = m.index ?? 0;
    if (offset >= start && offset <= start + m[0].length) return m[0].toLowerCase();
  }
  return null;
}

// The display name a thread message's headers give for an address, or '' when it appears bare
// (or not at all — e.g. an address mentioned in the body).
function headerNameFor(email: string, msg: ThreadMessage | undefined): string {
  if (!msg) return '';
  for (const raw of [msg.from_address, msg.to_address, msg.cc_address]) {
    const hit = parseAddressList(raw).find((a) => a.email === email);
    if (hit?.name) return hit.name;
  }
  return '';
}

interface CommunicationCardProps {
  entry: CommEntry;
  isExpanded: boolean;
  isSelectedForReply: boolean;
  showContactName: boolean;
  onToggleExpand: (key: string) => void;
  onDeleteConfirm: (contactId: string, commIndex: number, gmailMessageId?: string, scheduledEmailId?: string) => void;
  onReschedule: (contactId: string, scheduledEmailId: string) => void;
  onSendNow: (contactId: string, scheduledEmailId: string, subject: string, to: string) => void;
  onThreadClick: (entry: CommEntry) => void;
  onMarkReadToggle: (contactId: string, commIndex: number, gmailMessageId: string | undefined, isCurrentlyRead: boolean) => void;
  onEmailContextMenu: (email: string, name: string) => void;
  commKey: string;
  bandMap: Record<string, string>;
}

const CommunicationCard = memo(function CommunicationCard({
  entry,
  isExpanded,
  isSelectedForReply,
  showContactName,
  onToggleExpand,
  onDeleteConfirm,
  onReschedule,
  onSendNow,
  onThreadClick,
  onMarkReadToggle,
  onEmailContextMenu,
  commKey,
  bandMap,
}: CommunicationCardProps) {
  const { contact, comm, index, threadMessage } = entry;
  const { user } = useAuth();
  const contentRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const date = threadMessage?.date || comm.email?.date_sent || comm.phone?.date || comm.form?.date || comm.other?.date || comm.thread?.last_message_date;
  const isSent = entryDirection(entry) === 'sent';
  const canDelete = comm.type === 'phone' || comm.type === 'form' || comm.type === 'other' || comm.type === 'email' || comm.type === 'thread';
  const isThreadEntry = !!threadMessage && !!comm.thread;
  const isSingleEmail = comm.type === 'email' && !!comm.email;
  const isScheduled = comm.email?.status === 'scheduled';
  const isFailed = comm.email?.status === 'failed';
  const isClickableEmail = !isScheduled && !isFailed && (isThreadEntry || isSingleEmail);
  const dateObj = date ? new Date(date) : null;
  // The date is tinted by its age, on the same ramp as the Venues `Last Contact` and Inbox
  // `Date` columns (docs/DATE_RECENCY_COLORS.md). A scheduled send is left out: its date is
  // its future send slot, where the ramp has nothing to say (it would clamp to "recent"
  // green), and the yellow clock already marks it as pending. It keeps the tooltip, which
  // reads forwards — "In 3 days".
  const dateColor = dateObj && !isScheduled ? recencyColor(dateObj) : undefined;
  const isEmailType = comm.type === 'email' || comm.type === 'thread';
  // Only mail I received has a read state (docs/CONTACT_IDENTITY.md). A thread mixes both
  // directions, so this is decided per message: my own messages, a scheduled send and a failed
  // one carry no toggle, exactly as their Inbox rows carry no read state.
  const hasReadState = isEmailType && !isSent && !isScheduled && !isFailed;
  const isRead = threadMessage ? threadMessage.is_read : comm.is_read;

  useEffect(() => {
    if (isExpanded || !contentRef.current) return;
    const el = contentRef.current;
    const raf = requestAnimationFrame(() => {
      if (el.isConnected) {
        setHasOverflow(el.scrollHeight > el.clientHeight);
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [isExpanded]);

  // Ctrl/⌘+click expands or collapses the full text, exactly like the chevron, and never falls
  // through to the plain-click behaviour of picking this message to reply to. Available on every
  // card, including the ones a plain click does nothing to (phone, form, scheduled, failed).
  const handleCardClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      onToggleExpand(commKey);
      return;
    }
    if (isClickableEmail) onThreadClick(entry);
  };

  // Right-click on an email address anywhere in the card (From/To/Cc lines, the body) hands it
  // up to open the New Contact form prefilled. Anywhere else the native context menu is kept.
  const handleContextMenu = (e: React.MouseEvent) => {
    const email = emailAtPoint(e.clientX, e.clientY);
    if (!email) return;
    e.preventDefault();
    onEmailContextMenu(email, headerNameFor(email, threadMessage));
  };

  return (
    <div
      onClick={handleCardClick}
      onContextMenu={handleContextMenu}
      className={clsx(
        'p-2 rounded-lg text-xs',
        !isEmailType
          ? 'bg-dark-elevated/50'
          : isFailed ? 'bg-red-900/15' : isScheduled ? 'bg-yellow-900/15' : !isSent ? 'bg-green-900/15' : 'bg-dark-elevated',
        isSelectedForReply
          ? 'border-2 border-primary-500'
          : isEmailType ? 'border border-dark-border' : 'border border-dashed border-dark-border',
        isClickableEmail && 'cursor-pointer hover:border-primary-600 transition-colors'
      )}
    >
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          {isScheduled && <Clock className="w-3 h-3 text-yellow-400" />}
          {isFailed && <AlertTriangle className="w-3 h-3 text-red-400" />}
          {(comm.type === 'email' || threadMessage) && !isScheduled && !isFailed && <MailIcon className="w-3 h-3" />}
          {comm.type === 'phone' && <Phone className="w-3 h-3" />}
          {comm.type === 'form' && <FileText className="w-3 h-3" />}
          {comm.type === 'other' && <User className="w-3 h-3" />}
          <span className="font-medium">
            {comm.type === 'email' && comm.email?.subject}
            {threadMessage && (threadMessage.subject || comm.thread?.subject)}
            {comm.type === 'phone' && 'Phone Call'}
            {comm.type === 'form' && 'Online Form'}
            {comm.type === 'other' && 'In Person'}
          </span>
          {comm.email?.status === 'scheduled' && (
            <Badge
              variant="warning"
              className="text-xs py-0 cursor-pointer hover:ring-1 hover:ring-yellow-400/50"
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); if (comm.email?.scheduled_email_id) onReschedule(contact._id, comm.email.scheduled_email_id); }}
              title="Click to reschedule"
            >
              Scheduled {comm.email.scheduled_at && format(new Date(comm.email.scheduled_at), 'MMM d, HH:mm')}
            </Badge>
          )}
          {isFailed && (
            <Badge variant="error" className="text-xs py-0" title={comm.email?.error || 'Send failed'}>
              Failed
            </Badge>
          )}
          {isSent && !isScheduled && !isFailed && <Badge variant="primary" className="text-xs py-0">Sent</Badge>}
          {!isSent && !isScheduled && !isFailed && (comm.email?.status === 'received' || comm.phone?.direction === 'received' || comm.other?.direction === 'received' || threadMessage) && (
            <Badge variant="success" className="text-xs py-0">Received</Badge>
          )}
          {comm.band_ids && comm.band_ids.length > 0 && comm.band_ids.map(id => (
            <Badge key={id} variant="info" className="text-xs py-0">{bandMap[id] || id}</Badge>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <Tooltip content={dateObj ? recencyLabel(dateObj) : undefined}>
            <span className="text-dark-text-muted whitespace-nowrap" style={{ color: dateColor }}>
              {dateObj && format(dateObj, 'MMM d, yyyy HH:mm')}
            </span>
          </Tooltip>
          {(threadMessage?.gmail_message_id || comm.email?.gmail_message_id || comm.thread?.gmail_thread_id) && (
            <a
              href={`https://mail.google.com/mail/u/?authuser=${encodeURIComponent(user?.email || '')}#inbox/${threadMessage?.gmail_message_id || comm.email?.gmail_message_id || comm.thread?.gmail_thread_id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="p-0.5 text-dark-text-muted hover:text-primary-400 hover:bg-dark-border rounded-sm transition-colors"
              title="Open in Gmail"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {isScheduled && comm.email?.scheduled_email_id && (
            <button
              onClick={(e) => { e.stopPropagation(); onReschedule(contact._id, comm.email!.scheduled_email_id!); }}
              className="p-0.5 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-900/20 rounded-sm"
              title="Reschedule"
            >
              <Calendar className="w-3 h-3" />
            </button>
          )}
          {(isScheduled || isFailed) && comm.email?.scheduled_email_id && (
            <button
              onClick={(e) => { e.stopPropagation(); onSendNow(contact._id, comm.email!.scheduled_email_id!, comm.email!.subject || '', contact.email || ''); }}
              className="p-0.5 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-sm"
              title={isFailed ? 'Retry send' : 'Send now'}
            >
              <Send className="w-3 h-3" />
            </button>
          )}
          {hasReadState && (
            <button
              // A standalone email is marked read per communication, not per message — its Gmail
              // id addresses no thread message, and the per-message endpoint would miss it.
              onClick={(e) => { e.stopPropagation(); onMarkReadToggle(contact._id, index, threadMessage?.gmail_message_id, !!isRead); }}
              className="p-0.5 text-dark-text-muted hover:text-primary-400 hover:bg-dark-border rounded-sm"
              title={isRead ? 'Mark as unread' : 'Mark as read'}
            >
              {isRead ? <MailOpen className="w-3 h-3 text-dark-text-muted" /> : <MailIcon className="w-3 h-3" />}
            </button>
          )}
          {canDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onDeleteConfirm(contact._id, index, threadMessage?.gmail_message_id, (isScheduled || isFailed) ? comm.email?.scheduled_email_id : undefined); }}
              className="p-0.5 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-sm"
              title={isScheduled ? 'Cancel scheduled email' : isFailed ? 'Dismiss failed email' : 'Delete'}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
      {showContactName && (
        <div className="text-dark-text-muted mb-1">
          <span className="font-medium">{contactDisplayName(contact)}</span>
          {contact.name && contact.email && <span> • {contact.email}</span>}
        </div>
      )}
      {threadMessage && (() => {
        // The owning contact is not always the sender: mail to a shared inbox is often
        // answered from a personal mailbox at the same domain, and that reply is stored
        // on the shared-inbox contact's thread. Surface the real From whenever it is
        // neither the user nor the contact already named above.
        const from = parseAddress(threadMessage.from_address);
        if (!from) return null;
        if (from.email === user?.email?.toLowerCase()) return null;
        if (from.email === contact.email?.toLowerCase()) return null;
        return (
          <div className="text-dark-text-muted mb-1">
            <span className="text-dark-text-secondary">From:</span>{' '}
            <span className="font-medium">{from.name || from.email}</span>
            {from.name && <span> • {from.email}</span>}
          </div>
        );
      })()}
      {threadMessage && (() => {
        const { to, cc } = otherRecipients(threadMessage, [user?.email, contact.email]);
        if (to.length === 0 && cc.length === 0) return null;
        return (
          <div className="text-dark-text-muted mb-1 flex flex-wrap gap-x-2">
            {to.length > 0 && <span><span className="text-dark-text-secondary">To:</span> {to.join(', ')}</span>}
            {cc.length > 0 && <span><span className="text-dark-text-secondary">Cc:</span> {cc.join(', ')}</span>}
          </div>
        );
      })()}
      <div className="flex items-end gap-1">
        <div
          ref={contentRef}
          className={clsx('text-dark-text-secondary flex-1 min-w-0', !isExpanded && 'line-clamp-2')}
        >
          {comm.email?.text && (
            <MailBody body={comm.email.text} messageId={comm.email.gmail_message_id} expanded={isExpanded} />
          )}
          {comm.phone?.commentary}
          {comm.form?.commentary}
          {comm.other?.commentary}
          {threadMessage && (
            <MailBody
              body={threadMessage.body}
              fallback={threadMessage.snippet}
              messageId={threadMessage.gmail_message_id}
              expanded={isExpanded}
            />
          )}
        </div>
        {(isExpanded || hasOverflow) && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleExpand(commKey); }}
            className="flex-shrink-0 p-0.5 text-dark-text-muted hover:text-dark-text-primary rounded-sm"
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
});

interface ContactOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  venue: Venue | null;
  contacts: Contact[];
  bands?: Band[];
  initialContactId?: string;
  onAddCommunication?: (contactId: string, communication: Communication) => Promise<void>;
  onRefreshContacts?: () => void;
  onRefreshVenues?: () => void;
  onEditVenue?: (venue: Venue) => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  isNavigating?: boolean;
  onNavigate?: (direction: 'prev' | 'next') => void;
}

export function ContactOverlay({
  isOpen,
  onClose,
  venue,
  contacts: initialContacts,
  bands: bandsList = [],
  initialContactId,
  onAddCommunication,
  onRefreshContacts,
  onRefreshVenues,
  onEditVenue,
  hasPrev,
  hasNext,
  isNavigating,
  onNavigate,
}: ContactOverlayProps) {
  const queryClient = useQueryClient();
  const invalidateTodos = useInvalidateTodos();
  const [contacts, setContacts] = useState<Contact[]>(initialContacts);
  const [localVenue, setLocalVenue] = useState<Venue | null>(venue);
  // The venue this open was the first ever look at, if any — its name is shown in bold
  const [firstVisitVenueId, setFirstVisitVenueId] = useState<string | null>(null);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<string>('all');
  const [directionFilter, setDirectionFilter] = useState<CommDirectionFilter>('all');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [todosCollapsed, setTodosCollapsed] = useState(false);
  const [expandedComms, setExpandedComms] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<{ contactId: string; commIndex: number; gmailMessageId?: string; scheduledEmailId?: string } | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<{ contactId: string; scheduledEmailId: string } | null>(null);
  const [sendNowConfirm, setSendNowConfirm] = useState<{ contactId: string; scheduledEmailId: string; subject: string; to: string } | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('10:30');
  const [isRescheduling, setIsRescheduling] = useState(false);

  const { user } = useAuth();
  const gmailGranted = user?.gmail_permissions_granted ?? false;

  // Contact sync state
  const [syncingContactId, setSyncingContactId] = useState<string | null>(null);
  const [syncDoneContactId, setSyncDoneContactId] = useState<string | null>(null);
  const [isSyncingAll, setIsSyncingAll] = useState(false);
  const hasAutoSyncedRef = useRef(false);

  // Contact edit/delete state
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editContactError, setEditContactError] = useState('');
  const [deleteContactConfirm, setDeleteContactConfirm] = useState<string | null>(null);
  // Adding the selected contact to an existing mailing list
  const [mailingListPickerOpen, setMailingListPickerOpen] = useState(false);
  const [isAddingToMailingList, setIsAddingToMailingList] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(false);

  // Which venue field the quick editor is on, and where it was summoned from — set by
  // right-clicking one of the venue card's field lines (type, styles, the three periods,
  // commentary). The same VenueFieldEditor the Venues page opens from a cell right-click.
  const [venueFieldEditor, setVenueFieldEditor] = useState<{ field: string; position: { x: number; y: number } } | null>(null);
  // An editor stacked over the overlay owns the keyboard while it is open: Escape and the
  // single-key shortcuts close or drive it, never the overlay underneath.
  const subEditorOpen = mailingListPickerOpen || venueFieldEditor !== null;

  // Interest editor state
  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [interestEditBandId, setInterestEditBandId] = useState<string | null>(null); // null = "all"
  const [interestEditLabel, setInterestEditLabel] = useState('');

  // New contact state
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newContactError, setNewContactError] = useState('');
  // Set when the form was opened by right-clicking an address in a message; null for the New button.
  const [newContactPrefill, setNewContactPrefill] = useState<Partial<ContactFormData> | null>(null);
  const rightPanelRef = useRef<HTMLDivElement>(null);
  const newContactFormRef = useRef<HTMLDivElement>(null);

  // Email templates state
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);

  // Thread reply state — which message the composer is answering
  const [replyingTo, setReplyingTo] = useState<ReplyTarget | null>(null);

  // Recomputed (fresh jitter included) only when the reschedule modal opens, so the times shown
  // don't shift under the user while it's sitting open.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const schedulePresets = useMemo(() => getSchedulePresets(), [rescheduleTarget]);

  useEffect(() => {
    setLocalVenue(venue);
  }, [venue]);

  // Venues created before coordinates existed have none stored. On open, try to resolve the
  // commune through BAN and persist it — but only on an unambiguous match, and only when core
  // fields are writable for this user. Entirely opportunistic: it never blocks or notifies.
  useEffect(() => {
    // BAN is French-only, so a foreign venue would just burn the retry schedule on a call that
    // cannot succeed — its map falls back to the country frame instead (VenueMiniMap).
    if (!isOpen || !venue || venue.coordinates || !venue.address?.city) return;
    if (!isFranceCountry(venue.address.country)) return;
    const venueId = venue._id;
    if ((geocodeAttemptedCount.get(venueId) ?? 0) >= GEOCODE_MAX_ATTEMPTS) return;

    const controller = new AbortController();
    let retryTimeout: ReturnType<typeof setTimeout> | undefined;
    const address = venue.address;
    // Mirrors can_modify_core in VenueService.update_venue — note refused_public stays writable
    // by its owner. Getting this wrong would just mean a guaranteed 403 on every overlay open.
    const canPersist =
      !!user?.is_admin ||
      (venue.owner_user_id === user?._id &&
        venue.visibility !== 'public' &&
        venue.visibility !== 'pending_public');

    const attempt = async () => {
      const attemptNumber = (geocodeAttemptedCount.get(venueId) ?? 0) + 1;
      geocodeAttemptedCount.set(venueId, attemptNumber);

      const coordinates = await geocodeVenueAddress(address, controller.signal);
      if (controller.signal.aborted) return;

      if (!coordinates) {
        // Indistinguishable here from a confirmed non-match, but retrying costs nothing when it
        // really is one — BAN answers those the same way every time.
        if (attemptNumber < GEOCODE_MAX_ATTEMPTS) {
          retryTimeout = setTimeout(attempt, GEOCODE_RETRY_DELAYS_MS[attemptNumber - 1]);
        }
        return;
      }

      // Show the pin even when we may not save it. Merge rather than replacing with the PATCH
      // response, which omits the list-only resolved_contacts / last_contact_date fields.
      setLocalVenue(prev => (prev && prev._id === venueId ? { ...prev, coordinates } : prev));
      if (!canPersist) return;

      try {
        await venuesAPI.update(venueId, { coordinates });
        onRefreshVenues?.();
      } catch (error) {
        console.error('Failed to persist venue coordinates:', error);
      }
    };

    attempt();

    return () => {
      controller.abort();
      if (retryTimeout) clearTimeout(retryTimeout);
    };
  }, [isOpen, venue, user, onRefreshVenues]);

  // Reads the venue's gatekeeper roles when the effect below fires, without making the venue a
  // reason to re-run it — that would reset the forms whenever the parent passes a fresh venue.
  const pickInitialContactId = useEffectEvent(
    (candidates: Contact[]) => selectBestContact(withVenueFlags(candidates, venue))._id,
  );

  useEffect(() => {
    setContacts(initialContacts);
    if (isOpen) {
      // Select initial contact if provided, otherwise first contact
      if (initialContacts.length > 0) {
        const contactToSelect = initialContactId && initialContacts.find(c => c._id === initialContactId)
          ? initialContactId
          : pickInitialContactId(initialContacts);
        setSelectedContactId(contactToSelect);
      }
      // Reset forms
      setShowNewContactForm(false);
      setNewContactPrefill(null);
      setIsEditingContact(false);
      setNewContactError('');
      setEditContactError('');
      setReplyingTo(null);
      // Fetch email templates
      emailTemplatesAPI.list().then(response => {
        setEmailTemplates(response.data.items);
      }).catch(error => {
        console.error('Failed to fetch email templates:', error);
      });
    }
  }, [initialContacts, isOpen, initialContactId]);

  // Compute selectedContact before hooks that depend on it
  const selectedContact = contacts.length > 0
    ? (contacts.find(c => c._id === selectedContactId) || contacts[0])
    : null;

  const bandMap = useMemo(() => {
    const map: Record<string, string> = {};
    bandsList.forEach(b => { map[b._id] = b.name; });
    return map;
  }, [bandsList]);

  const hasContacts = contacts.length > 0;
  // The selected contact's flag at this venue: gatekeeper is a role held per venue
  const contactFlag = selectedContact ? effectiveFlag(selectedContact, localVenue) : 'none';
  // Memoized: the edit form resets whenever its contact prop changes identity
  const selectedContactAtVenue = useMemo(
    () => (selectedContact ? { ...selectedContact, flags: contactFlag } : null),
    [selectedContact, contactFlag],
  );
  const isEmailBlocked = contactFlag === 'do_not_contact' || contactFlag === 'expired';
  const emailBlockedReason = contactFlag === 'do_not_contact'
    ? 'Contact is marked as "do not contact"'
    : contactFlag === 'expired'
      ? 'Contact is marked as expired'
      : undefined;

  // Auto-sync all contacts with email when the overlay opens for a venue
  useEffect(() => {
    if (!isOpen) {
      hasAutoSyncedRef.current = false;
      return;
    }
    if (hasAutoSyncedRef.current || !venue || !gmailGranted || contacts.length === 0) return;

    const contactsWithEmail = contacts.filter(
      c => c.email && c.flags !== 'do_not_contact' && c.flags !== 'expired'
    );
    if (contactsWithEmail.length === 0) return;

    hasAutoSyncedRef.current = true;

    const runAutoSync = async () => {
      setIsSyncingAll(true);
      const toastId = toast.loading('Syncing contacts...');
      try {
        const results = await Promise.allSettled(
          contactsWithEmail.map(c => contactsAPI.syncCommunications(c._id))
        );
        const newThreads = results.reduce((sum, r) => {
          if (r.status === 'fulfilled') return sum + (r.value.data.new_threads ?? 0);
          return sum;
        }, 0);
        const refreshed = await Promise.allSettled(
          contactsWithEmail.map(c => contactsAPI.getById(c._id))
        );
        refreshed.forEach((r, i) => {
          if (r.status === 'fulfilled') {
            setContacts(prev => prev.map(c => c._id === contactsWithEmail[i]._id ? r.value.data : c));
          }
        });
        onRefreshContacts?.();
        const msg = newThreads > 0 ? `Sync complete — ${newThreads} new !` : 'Sync complete';
        toast.success(
          (t) => (
            <span className="flex items-center gap-3">
              <span>{msg}</span>
              <button onClick={() => toast.dismiss(t.id)} className="text-slate-400 hover:text-white text-xl leading-none font-light" aria-label="Dismiss">×</button>
            </span>
          ),
          { id: toastId, duration: Infinity }
        );
      } catch {
        toast.error('Sync failed', { id: toastId });
      } finally {
        setIsSyncingAll(false);
      }
    };

    runAutoSync();
  }, [isOpen, venue, contacts, gmailGranted]); // eslint-disable-line react-hooks/exhaustive-deps

  const allCommunications = useMemo((): CommEntry[] => {
    const allComms: CommEntry[] = [];

    const contactsToShow = historyFilter === 'all' ? contacts : contacts.filter(c => c._id === historyFilter);

    // A Gmail message held by several of the venue's contacts (a colleague replying in a thread
    // sent to the town hall) is listed once, under the contact it was exchanged with.
    const atByMessageId = new Map<string, number>();
    const add = (entry: CommEntry, messageId: string | null | undefined) => {
      const at = messageId ? atByMessageId.get(messageId) : undefined;
      if (at === undefined) {
        if (messageId) atByMessageId.set(messageId, allComms.length);
        allComms.push(entry);
      } else if (isExchangedWith(entry) && !isExchangedWith(allComms[at])) {
        allComms[at] = entry;
      }
    };

    contactsToShow.forEach(contact => {
      contact.communications?.forEach((comm, index) => {
        if (comm.type === 'thread' && comm.thread?.messages && comm.thread.messages.length > 0) {
          // Flatten: one entry per message in the thread
          comm.thread.messages.forEach(msg => {
            add({ contact, comm, index, threadMessage: msg }, msg.gmail_message_id);
          });
        } else {
          add({ contact, comm, index }, comm.email?.gmail_message_id);
        }
      });
    });

    // Sort by date descending
    allComms.sort((a, b) => {
      const getDate = (entry: CommEntry) =>
        entry.threadMessage?.date || entry.comm.email?.date_sent || entry.comm.phone?.date || entry.comm.form?.date || entry.comm.other?.date || entry.comm.thread?.last_message_date || '';
      return new Date(getDate(b)).getTime() - new Date(getDate(a)).getTime();
    });

    if (directionFilter === 'all') return allComms;
    return allComms.filter(entry => entryDirection(entry) === directionFilter);
  }, [contacts, historyFilter, directionFilter]);

  useEffect(() => {
    if (allCommunications.length === 0) return;
    const first = allCommunications[0];
    const key = `${first.contact._id}-${first.index}-${first.threadMessage?.gmail_message_id ?? ''}-0`;
    setExpandedComms(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  }, [allCommunications]);

  useEffect(() => {
    if (!isOpen || !onNavigate || subEditorOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;
      if (e.key === 'ArrowLeft' && hasPrev && !isNavigating) onNavigate('prev');
      if (e.key === 'ArrowRight' && hasNext && !isNavigating) onNavigate('next');
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onNavigate, hasPrev, hasNext, isNavigating, subEditorOpen]);

  // Opening the overlay is what makes a venue "seen", which stops the Venues page showing its
  // name in bold (docs/VENUE_SEEN.md). The save is fire-and-forget: a failure is silent and
  // simply retried the next time the venue is opened, since nothing on screen depends on the mark
  // having been written at that instant.
  //
  // The venue whose mark this open wrote is remembered, so the header keeps showing its name in
  // bold for as long as it is on screen: the mark is already saved by then, and the title would
  // otherwise un-bold itself the moment it was read.
  //
  // The cached rows wait for the close (`flushSeenPatch`). Patching them on open would be
  // invisible — this modal covers the table — and would re-render the whole overlay just as it
  // opens, since the venue lists it patches are what `VenuesPage` renders from.
  const pendingSeenPatch = useRef(new Set<string>());
  const flushSeenPatch = useCallback(() => {
    const ids = pendingSeenPatch.current;
    if (ids.size === 0) return;
    // `invalidate: false`: no filter and no sort reads `seen`, so the patch is the whole story and
    // marking the lists stale would only cost the Venues table a blocking refetch.
    patchCachedVenueFields(queryClient, [...ids], { seen: true }, { invalidate: false });
    ids.clear();
  }, [queryClient]);

  useEffect(() => {
    // The overlay stays mounted between opens, so the marker has to be dropped on close —
    // otherwise reopening the same venue, now seen, would still come up bold. The close is also
    // when the rows marked during this session catch up, prev/next included.
    if (!isOpen) {
      setFirstVisitVenueId(null);
      flushSeenPatch();
      return;
    }
    if (!venue || venue.seen || markedSeen.has(venue._id)) return;
    const venueId = venue._id;
    markedSeen.add(venueId);
    setFirstVisitVenueId(venueId);
    // Queued before the save answers, not after it: closing the overlay within the round-trip is
    // far more likely than the save failing, and waiting would leave that row bold with the mark
    // already stored. A failure drops it again — or, if the flush beat it, leaves a row shown as
    // seen that the next real fetch puts back in bold, and that the next open retries.
    pendingSeenPatch.current.add(venueId);
    venuesAPI.setSeen(venueId).catch(() => {
      markedSeen.delete(venueId);
      pendingSeenPatch.current.delete(venueId);
    });
  }, [isOpen, venue, queryClient, flushSeenPatch]);

  // Navigating away from the page unmounts the overlay without ever closing it, so the rows it
  // marked would otherwise stay bold in whatever cached list outlives it.
  useEffect(() => flushSeenPatch, [flushSeenPatch]);

  // The venue's flag (docs/VENUE_FLAGS.md): flipped on screen at once, saved in the background,
  // undone if the save fails. A venue's saves are chained so fast toggles land in order.
  const flagSavesRef = useRef(new Map<string, Promise<unknown>>());
  const handleToggleFlag = useCallback(() => {
    if (!localVenue) return;
    const venueId = localVenue._id;
    const flagged = !localVenue.flagged;
    const show = (value: boolean) => {
      setLocalVenue(v => (v && v._id === venueId ? { ...v, flagged: value } : v));
      patchCachedVenueFlags(queryClient, [venueId], value);
    };
    show(flagged);
    toast.loading(flagged ? 'Flagging venue...' : 'Removing flag...', { id: FLAG_TOAST_ID });

    const saves = flagSavesRef.current;
    const save = (saves.get(venueId) ?? Promise.resolve())
      .catch(() => {})
      .then(() => venuesAPI.setFlag(venueId, flagged));
    saves.set(venueId, save);
    save.then(
      () => toast.success(flagged ? 'Venue flagged' : 'Flag removed', { id: FLAG_TOAST_ID, duration: 1500 }),
      () => {
        // A later toggle already set the value to show; only the latest save may undo
        if (saves.get(venueId) === save) show(!flagged);
        toast.error('Failed to update the flag', { id: FLAG_TOAST_ID });
      },
    ).finally(() => {
      if (saves.get(venueId) === save) saves.delete(venueId);
    });
  }, [localVenue, queryClient]);

  const toggleDirectionFilter = useCallback((direction: 'sent' | 'received') => {
    setDirectionFilter(current => (current === direction ? 'all' : direction));
  }, []);

  // "p" and "t" mirror the Previous Communications and Todos header toggles; "f" the venue flag;
  // "s" and "r" the Sent and Received counts (docs/SHORTCUTS.md).
  useEffect(() => {
    if (!isOpen || subEditorOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e)) return;
      const key = e.key.toLowerCase();
      if (key === 'f') {
        e.preventDefault();
        handleToggleFlag();
      } else if (key === 'p') {
        e.preventDefault();
        setHistoryExpanded(v => !v);
      } else if (key === 's' || key === 'r') {
        e.preventDefault();
        toggleDirectionFilter(key === 's' ? 'sent' : 'received');
      } else if (key === 't') {
        e.preventDefault();
        // The todos section is hidden entirely while the history is expanded, so collapse the
        // history first — "t" should always end with the todos on screen.
        if (historyExpanded) {
          setHistoryExpanded(false);
          setTodosCollapsed(false);
        } else {
          setTodosCollapsed(v => !v);
        }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, historyExpanded, handleToggleFlag, toggleDirectionFilter, subEditorOpen]);

  const handleToggleExpand = useCallback((key: string) => {
    setExpandedComms(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleDeleteConfirm = useCallback((contactId: string, commIndex: number, gmailMessageId?: string, scheduledEmailId?: string) => {
    setDeleteConfirm({ contactId, commIndex, gmailMessageId, scheduledEmailId });
  }, []);

  const handleRescheduleOpen = useCallback((contactId: string, scheduledEmailId: string) => {
    setRescheduleTarget({ contactId, scheduledEmailId });
    setRescheduleDate('');
    setRescheduleTime('10:30');
  }, []);

  const handleSendNowOpen = useCallback((contactId: string, scheduledEmailId: string, subject: string, to: string) => {
    setSendNowConfirm({ contactId, scheduledEmailId, subject, to });
  }, []);

  const handleMarkReadToggle = useCallback(async (contactId: string, commIndex: number, gmailMessageId: string | undefined, isCurrentlyRead: boolean) => {
    // Optimistic update
    setContacts(prev => prev.map(c => {
      if (c._id !== contactId) return c;
      const communications = c.communications?.map((comm, i) => {
        if (i !== commIndex) return comm;
        if (comm.type === 'thread' && gmailMessageId) {
          return {
            ...comm,
            thread: comm.thread ? {
              ...comm.thread,
              messages: comm.thread.messages.map(msg =>
                msg.gmail_message_id === gmailMessageId ? { ...msg, is_read: !isCurrentlyRead } : msg
              ),
            } : comm.thread,
          };
        }
        return { ...comm, is_read: !isCurrentlyRead };
      });
      return { ...c, communications };
    }));

    try {
      if (isCurrentlyRead) {
        if (gmailMessageId) {
          await contactsAPI.markMessageUnread(contactId, commIndex, gmailMessageId);
        } else {
          await contactsAPI.markCommunicationUnread(contactId, commIndex);
        }
      } else {
        if (gmailMessageId) {
          await contactsAPI.markMessageRead(contactId, commIndex, gmailMessageId);
        } else {
          await contactsAPI.markCommunicationRead(contactId, commIndex);
        }
      }
      // Refresh the navbar unread badge (server recomputes the unread received count)
      queryClient.invalidateQueries({ queryKey: ['inbox', 'unread-count'] });
    } catch {
      contactsAPI.getById(contactId).then(r => {
        setContacts(prev => prev.map(c => c._id === contactId ? r.data : c));
      }).catch(() => {});
    }
  }, [queryClient]);

  const handleSendNowConfirm = async () => {
    if (!sendNowConfirm) return;
    try {
      await gmailAPI.sendNowScheduled(sendNowConfirm.scheduledEmailId);
      // Dispatch is when a scheduled email's follow-up todo is written (docs/EMAIL_SEND.md).
      invalidateTodos();
      const response = await contactsAPI.getById(sendNowConfirm.contactId);
      setContacts(prev => prev.map(c => c._id === sendNowConfirm.contactId ? response.data : c));
      onRefreshContacts?.();
    } catch (error) {
      console.error('Failed to send email now:', error);
    } finally {
      setSendNowConfirm(null);
    }
  };

  const handleRescheduleConfirm = async (isoDate: string) => {
    if (!rescheduleTarget) return;
    setIsRescheduling(true);
    try {
      await gmailAPI.rescheduleScheduled(rescheduleTarget.scheduledEmailId, isoDate);
      const response = await contactsAPI.getById(rescheduleTarget.contactId);
      setContacts(prev => prev.map(c => c._id === rescheduleTarget.contactId ? response.data : c));
      onRefreshContacts?.();
    } catch (error) {
      console.error('Failed to reschedule email:', error);
    } finally {
      setIsRescheduling(false);
      setRescheduleTarget(null);
    }
  };

  const handleRescheduleCustom = () => {
    if (!rescheduleDate || !rescheduleTime) return;
    const dt = new Date(`${rescheduleDate}T${rescheduleTime}:00`);
    handleRescheduleConfirm(dt.toISOString());
  };

  // Right-click on an address in Previous Communications. An address that already belongs to one
  // of this venue's contacts selects that contact (the API has no duplicate check); any other opens
  // the New Contact form with the email — and the header name, when there is one — filled in.
  const handleEmailContextMenu = useCallback((email: string, name: string) => {
    const existing = contacts.find(c => c.email?.toLowerCase() === email);
    if (existing) {
      setSelectedContactId(existing._id);
      setReplyingTo(null);
      setShowNewContactForm(false);
      setNewContactPrefill(null);
      toast(`${contactDisplayName(existing)} is already a contact of this venue`);
      return;
    }
    setNewContactPrefill({ email, name });
    setNewContactError('');
    setIsEditingContact(false);
    setShowNewContactForm(true);
  }, [contacts]);

  // The form opens at the top of the right panel, which scrolls: a right-click made with the panel
  // scrolled down would otherwise fill in a form the user cannot see. Only the panel is scrolled
  // (not the page), and only for the right-click path — the New button is itself next to the form.
  useEffect(() => {
    if (!showNewContactForm || !newContactPrefill) return;
    const raf = requestAnimationFrame(() => {
      const panel = rightPanelRef.current;
      const form = newContactFormRef.current;
      if (!panel || !form) return;
      const delta = form.getBoundingClientRect().top - panel.getBoundingClientRect().top;
      panel.scrollTo({ top: panel.scrollTop + delta - 8, behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(raf);
  }, [showNewContactForm, newContactPrefill]);

  // Picking a message only records *which* message. The composer derives the subject, the tab
  // and the template reset from it — reaching into its state from here is what kept that state
  // in the overlay, and with it the re-render of everything on every keystroke.
  const handleThreadClick = useCallback((entry: CommEntry) => {
    const { comm, threadMessage } = entry;

    // Handle single email click
    if (comm.type === 'email' && comm.email) {
      const messageId = comm.email.gmail_message_id || '';
      const subject = comm.email.subject;
      setReplyingTo(prev =>
        prev?.messageId === messageId && !prev?.threadId
          ? null
          : { threadId: '', messageId, subject });
      return;
    }

    // Handle thread message click
    if (!comm.thread || !threadMessage) return;
    const threadId = comm.thread.gmail_thread_id;
    const messageId = threadMessage.gmail_message_id;
    const subject = threadMessage.subject || comm.thread.subject;
    setReplyingTo(prev =>
      prev?.threadId === threadId && prev?.messageId === messageId
        ? null
        : { threadId, messageId, subject });
  }, []);

  const clearReplyingTo = useCallback(() => setReplyingTo(null), []);

  /** A communication the composer saved: the contact comes back as the server stored it. */
  const handleCommunicationSaved = useCallback(
    async (contactId: string, updated: Contact, communication: Communication) => {
      setContacts(prev => prev.map(c => (c._id === contactId ? updated : c)));
      await onAddCommunication?.(contactId, communication);
      onRefreshContacts?.();
    },
    [onAddCommunication, onRefreshContacts],
  );

  if (!isOpen) return null;

  if (!localVenue && !venue && !hasContacts) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Contact"
        size="full"
      >
        <div className="flex items-center justify-center h-full text-dark-text-muted">
          No venue selected
        </div>
      </Modal>
    );
  }

  // Calculate venue statistics
  const calculateStats = () => {
    let lastContact: string | undefined;

    // Only one tier of contacts speaks for the venue — the same rule the venues table's columns
    // follow, so the overlay never contradicts the row it was opened from. All three stats are
    // read off the contacts in hand rather than off the row, because a venue response from a
    // write (a favourite, a contact unlinked) carries no rollup to replace the row's with.
    const { pool, source } = lastContactPool(withVenueFlags(contacts, localVenue));
    const { sent: totalSent, received: totalReceived } = countPoolDirections(pool);

    pool.forEach(contact => {
      contact.communications?.forEach(comm => {
        const date = comm.email?.date_sent || comm.phone?.date || comm.form?.date || comm.other?.date || comm.thread?.last_message_date;
        if (date && (!lastContact || new Date(date) > new Date(lastContact))) {
          lastContact = date;
        }
      });
    });

    const lastContactNote = source ? LAST_CONTACT_SOURCE_NOTE[source] : undefined;

    return { totalSent, totalReceived, lastContact, lastContactNote };
  };

  const stats = calculateStats();

  const handleDeleteCommunication = async (contactId: string, commIndex: number, gmailMessageId?: string, scheduledEmailId?: string) => {
    try {
      if (scheduledEmailId) {
        await gmailAPI.cancelScheduled(scheduledEmailId);
        // Cancelling the last email of a campaign that never sent anything takes its follow-up
        // todo with it (docs/CAMPAIGNS.md) — the badge and the Todo page have to hear about it.
        invalidateTodos();
        const response = await contactsAPI.getById(contactId);
        setContacts(prev => prev.map(c => c._id === contactId ? response.data : c));
      } else {
        const response = await contactsAPI.deleteCommunication(contactId, commIndex, gmailMessageId);
        setContacts(prev => prev.map(c => c._id === contactId ? response.data : c));
      }
      onRefreshContacts?.();
    } catch (error) {
      console.error('Failed to delete communication:', error);
    }
    setDeleteConfirm(null);
  };

  const handleSyncContact = async (contactId: string) => {
    setSyncingContactId(contactId);
    setSyncDoneContactId(null);
    try {
      await contactsAPI.syncCommunications(contactId);
      setSyncDoneContactId(contactId);
      onRefreshContacts?.();
      // Refresh local contact data
      const response = await contactsAPI.getById(contactId);
      setContacts(prev => prev.map(c => c._id === contactId ? response.data : c));
      // Clear the done state after 2 seconds
      setTimeout(() => setSyncDoneContactId(null), 2000);
    } catch (error) {
      console.error('Failed to sync contact communications:', error);
    } finally {
      setSyncingContactId(null);
    }
  };

  // Contact edit/delete handlers
  const handleStartEditContact = () => {
    if (selectedContact) {
      setEditContactError('');
      setIsEditingContact(true);
    }
  };

  // Append the selected contact to an existing mailing list (same shape the Venues bulk action
  // sends: the full contact array, deduped on contact_id).
  const handleAddContactToMailingList = async (list: MailingList) => {
    if (!selectedContact?.email) return;
    if (list.contacts.some((c) => c.contact_id === selectedContact._id)) {
      toast(`Already in "${list.name}"`);
      setMailingListPickerOpen(false);
      return;
    }
    setIsAddingToMailingList(true);
    try {
      await mailingListsAPI.update(list._id, {
        contacts: [...list.contacts, { contact_id: selectedContact._id, email: selectedContact.email }],
      });
      queryClient.invalidateQueries({ queryKey: ['mailing-lists'] });
      toast.success(`Added ${contactDisplayName(selectedContact)} to "${list.name}"`);
      setMailingListPickerOpen(false);
    } catch (error) {
      console.error('Failed to add contact to mailing list:', error);
      toast.error('Failed to add the contact to the list');
    } finally {
      setIsAddingToMailingList(false);
    }
  };

  const handleSaveContact = async (data: ContactFormData) => {
    if (!selectedContact) return;

    // Build update payload
    const updateData: Record<string, unknown> = {
      name: data.name.trim(),
      is_generic: data.is_generic,
      flags: data.flags,
    };

    // Sent whether or not they still hold text: an emptied field is one the user cleared, and
    // leaving it out of the PATCH reads as "leave it alone", so the old value would come back.
    // The address is the exception — it identifies the contact and an empty one is rejected.
    updateData.phone = data.phone.trim();
    updateData.function = data.function.trim();
    updateData.commentary = data.commentary.trim();
    if (data.email.trim()) updateData.email = data.email.trim();

    const contactId = selectedContact._id;
    try {
      // The flag is chosen at this venue: gatekeeper sets or lifts the role here only
      const response = await contactsAPI.update(contactId, updateData, localVenue?._id);
      setContacts(prev => prev.map(c => c._id === contactId ? response.data : c));
    } catch (error) {
      offerMergeOnEmailInUse(error, contactId);
      throw error; // the form shows why the save failed
    }
    if (data.flags !== contactFlag) await refreshVenueRoles();
    setIsEditingContact(false);
    onRefreshContacts?.();
  };

  /** Re-read the venue's gatekeeper roles and links after a change made through one of its contacts. */
  const refreshVenueRoles = async () => {
    if (!localVenue) return;
    const venueId = localVenue._id;
    try {
      const { data: fresh } = await venuesAPI.getById(venueId);
      // Merge: the GET omits the list-only fields (resolved_contacts, last_contact_date)
      setLocalVenue(prev => (prev && prev._id === venueId
        ? { ...prev, contact_ids: fresh.contact_ids, gatekeeper_contact_ids: fresh.gatekeeper_contact_ids ?? [] }
        : prev));
    } catch (error) {
      console.error('Failed to refresh the venue roles:', error);
    }
  };

  /**
   * An email can belong to one contact only. Saving another contact's email is refused (409
   * email_in_use); offer to merge the edited contact into the one that holds it.
   */
  const offerMergeOnEmailInUse = (error: unknown, contactId: string) => {
    const response = (error as { response?: { status?: number; data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail as { code?: string; existing_contact_id?: string } | undefined;
    const existingId = detail?.existing_contact_id;
    if (response?.status !== 409 || detail?.code !== 'email_in_use' || !existingId) return;
    toast((t) => (
      <span className="flex items-center gap-3">
        <span>This email already belongs to another contact.</span>
        <button
          type="button"
          className="btn btn-primary text-xs whitespace-nowrap"
          onClick={() => { toast.dismiss(t.id); void mergeIntoExisting(contactId, existingId); }}
        >
          Merge them
        </button>
      </span>
    ), { duration: 10000 });
  };

  /** Merge a contact into the one holding its address: the latter keeps both histories and links. */
  const mergeIntoExisting = async (contactId: string, existingId: string) => {
    try {
      const { data: merged } = await contactsAPI.merge(contactId, existingId);
      setContacts(prev => [...prev.filter(c => c._id !== contactId && c._id !== existingId), merged]);
      setSelectedContactId(existingId);
      setIsEditingContact(false);
      await refreshVenueRoles();
      onRefreshContacts?.();
      onRefreshVenues?.();
      toast.success('Merged into the existing contact');
    } catch (error) {
      console.error('Failed to merge contacts:', error);
      toast.error('Could not merge the contacts');
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!localVenue) return;

    const contact = contacts.find(c => c._id === contactId);
    const isLastVenue = !contact || contact.venue_ids.length <= 1;

    setIsDeletingContact(true);
    try {
      if (isLastVenue) {
        // Delete the contact entirely — backend cascades removal from all linked venues, and
        // drops the contact's todos with it (ContactService.soft_delete_contact), which the
        // panel right above has to be told about.
        await contactsAPI.delete(contactId);
        invalidateTodos();
      } else {
        // Contact belongs to multiple venues — only dissociate from this venue
        await venuesAPI.removeContact(localVenue._id, contactId);
      }

      // Update local state
      setContacts(prev => prev.filter(c => c._id !== contactId));

      // If removed the selected contact, select another one
      if (selectedContactId === contactId) {
        const remaining = contacts.filter(c => c._id !== contactId);
        setSelectedContactId(remaining.length > 0 ? remaining[0]._id : null);
      }

      onRefreshContacts?.();
      onRefreshVenues?.();

      // Close modal if no contacts left
      if (contacts.length === 1) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to delete/dissociate contact:', error);
    } finally {
      setIsDeletingContact(false);
      setDeleteContactConfirm(null);
    }
  };

  const handleCreateNewContact = async (data: ContactFormData) => {
    if (!localVenue) return;

    // Build payload - only include non-empty fields
    const payload: Record<string, unknown> = {
      name: data.name.trim(),
      is_generic: data.is_generic,
      flags: data.flags,
    };

    if (data.email.trim()) payload.email = data.email.trim();
    if (data.phone.trim()) payload.phone = data.phone.trim();
    if (data.function.trim()) payload.function = data.function.trim();
    if (data.commentary.trim()) payload.commentary = data.commentary.trim();

    // Created at this venue: linked to it, a gatekeeper flag becoming its role here. An email the
    // user already has a contact for brings that contact back rather than a second one.
    const response = await contactsAPI.create(payload, localVenue._id);
    const { reused, ...newContact } = response.data;

    // Update local state
    setContacts(prev => [...prev.filter(c => c._id !== newContact._id), newContact]);
    setSelectedContactId(newContact._id);
    setShowNewContactForm(false);
    setNewContactPrefill(null);
    if (reused) toast.success(`Linked your existing contact for ${newContact.email}`);
    await refreshVenueRoles();

    onRefreshContacts?.();
    onRefreshVenues?.();
  };

  const getTimeAgo = (date: string) => {
    const now = new Date();
    const past = new Date(date);
    const diffInDays = Math.floor((now.getTime() - past.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return '1 day ago';
    if (diffInDays < 30) return `${diffInDays} days ago`;
    if (diffInDays < 365) {
      const months = Math.floor(diffInDays / 30);
      return `${months} ${months === 1 ? 'month' : 'months'} ago`;
    }
    const years = Math.floor(diffInDays / 365);
    return `${years} ${years === 1 ? 'year' : 'years'} ago`;
  };

  /**
   * The bands a mail just sent named are already favorites of this venue server-side — the send
   * applies the rule the Gmail sync applies to a band the venue names back. Nothing was asked of
   * the server here, so this only catches the stars up, as the toggle below does after its write.
   */
  const handleFavoritedBands = (bandIds: string[]) => {
    if (!localVenue) return;
    const favIds = localVenue.favorite_band_ids || [];
    const added = bandIds.filter(id => !favIds.includes(id));
    if (added.length === 0) return;

    setLocalVenue({ ...localVenue, favorite_band_ids: [...favIds, ...added] });
    onRefreshVenues?.();
  };

  const handleToggleFavorite = async (bandId: string) => {
    if (!localVenue) return;
    const venueId = localVenue._id;
    const previousVenue = localVenue;
    const favIds = localVenue.favorite_band_ids || [];
    const newFavIds = favIds.includes(bandId)
      ? favIds.filter(id => id !== bandId)
      : [...favIds, bandId];

    // Optimistic update
    setLocalVenue({ ...localVenue, favorite_band_ids: newFavIds });

    try {
      const response = await venuesAPI.toggleFavorite(venueId, bandId);
      setLocalVenue(response.data);
      onRefreshVenues?.();
    } catch (error) {
      setLocalVenue(previousVenue);
      console.error('Failed to toggle favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  const getInterestForBand = (bandId: string | null): BandInterest => {
    const interest = (localVenue?.interests || []).find(i => i.band_id === bandId);
    return interest || { band_id: bandId, level: 'no_information' };
  };

  const handleOpenInterestModal = (bandId: string | null, label: string) => {
    setInterestEditBandId(bandId);
    setInterestEditLabel(label);
    setInterestModalOpen(true);
  };

  // The interest and the venue's pause travel in one update: the server keeps them in step, so
  // sending them apart would let a half-applied state through (docs/VENUE_PAUSE.md).
  const handleSaveInterest = async (level: InterestLevel, comment: string, recontact?: RecontactRequest | null) => {
    if (!localVenue) return;
    const interests = [...(localVenue.interests || [])];
    const idx = interests.findIndex(i => i.band_id === interestEditBandId);
    const entry: BandInterest = { band_id: interestEditBandId, level, comment: comment || undefined };
    if (idx >= 0) {
      interests[idx] = entry;
    } else {
      interests.push(entry);
    }
    try {
      const payload = recontact === undefined ? { interests } : { interests, recontact };
      const response = await venuesAPI.update(localVenue._id, payload);
      setLocalVenue(response.data);
      // Patched rather than refetched: a refetch would drop a newly paused venue out of the
      // default "not hidden" list while the overlay is still paging through it.
      patchCachedVenueFields(queryClient, [localVenue._id], {
        interests: response.data.interests,
        recontact: response.data.recontact ?? null,
      });
      onRefreshVenues?.();
    } catch (error) {
      console.error('Failed to update interest:', error);
      toast.error('Failed to save the interest');
    }
  };

  /**
   * The right-click wiring shared by the venue card's field lines: each one opens
   * VenueFieldEditor on its own field, exactly as a cell right-click does on the Venues page.
   * `venueFieldRow` carries the field column's layout with it; the commentary column beside it
   * has its own, so it takes the handler alone.
   */
  const openVenueFieldEditor = (field: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    setVenueFieldEditor({ field, position: { x: e.clientX, y: e.clientY } });
  };

  const venueFieldRow = (field: string, name: string, layout = 'items-center') => ({
    className: clsx('flex gap-1 cursor-context-menu rounded-sm hover:bg-dark-border/40', layout),
    title: `Right-click to edit ${name}`,
    onContextMenu: openVenueFieldEditor(field),
  });

  // A field edited from the venue card's right-click editor. Only the one field the editor was
  // opened on ever comes back, so the cached copies are patched over rather than refetched —
  // the same way the interest above is, and for the same reason: a refetch here can reshuffle
  // the list the overlay is paging through.
  const handleSaveVenueField = async (venueId: string, data: Partial<VenueFormData>) => {
    try {
      const response = await venuesAPI.update(venueId, data);
      setLocalVenue(prev => (prev && prev._id === venueId ? response.data : prev));
      const patch = Object.fromEntries(
        Object.keys(data)
          .filter(key => key in response.data)
          .map(key => [key, response.data[key as keyof Venue]]),
      ) as Partial<Venue>;
      patchCachedVenueFields(queryClient, [venueId], patch);
      onRefreshVenues?.();
    } catch (error) {
      console.error('Failed to update venue field:', error);
      toast.error('Failed to save the change');
    }
  };

  const navActions = onNavigate ? (
    <div className="flex items-center gap-0.5">
      <button
        onClick={() => onNavigate('prev')}
        disabled={!hasPrev || isNavigating}
        className="p-1 rounded-sm transition-colors hover:bg-dark-border disabled:opacity-30 disabled:cursor-not-allowed"
        title="Previous"
      >
        <ChevronLeft className="w-4 h-4 text-dark-text-secondary" />
      </button>
      <button
        onClick={() => onNavigate('next')}
        disabled={!hasNext || isNavigating}
        className="p-1 rounded-sm transition-colors hover:bg-dark-border disabled:opacity-30 disabled:cursor-not-allowed"
        title="Next"
      >
        <ChevronRight className="w-4 h-4 text-dark-text-secondary" />
      </button>
    </div>
  ) : undefined;

  const isVenueInactive = localVenue?.status === 'closed' || localVenue?.status === 'no_concerts';

  const flagToggle = localVenue ? (
    <button
      onClick={handleToggleFlag}
      aria-pressed={Boolean(localVenue.flagged)}
      className={clsx(
        'p-1 rounded-sm transition-colors hover:bg-dark-border',
        localVenue.flagged ? 'text-red-500' : 'text-dark-text-muted hover:text-dark-text-secondary',
      )}
      title={localVenue.flagged ? 'Unflag venue (f)' : 'Flag venue (f)'}
    >
      <Flag className="w-4 h-4" fill={localVenue.flagged ? 'currentColor' : 'none'} />
    </button>
  ) : undefined;

  const venueWebsiteUrl = externalUrl(localVenue?.url);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        // Escape/backdrop belongs to whatever editor sits over the overlay — it closes itself
        // on the same keypress, so the overlay must not close underneath it.
        if (subEditorOpen) return;
        onClose();
      }}
      title={localVenue?.name || venue?.name || selectedContact?.name || 'Contact'}
      // Bold while this is the first look at the venue, as its row on the Venues page was until
      // this open marked it seen (docs/VENUE_SEEN.md)
      titleClassName={localVenue && firstVisitVenueId === localVenue._id ? 'font-bold' : undefined}
      titleAddon={flagToggle}
      size="full"
      headerActions={navActions}
    >
      <div className="flex gap-6 h-full">
        {/* Left Column - Venue Info & History */}
        <div className="w-1/2 flex flex-col gap-3 overflow-hidden">
          {/* Venue Info Card */}
          {localVenue && !historyExpanded && (
            // Inactive venues are greyed out and struck through, as their rows are on the Venues page
            <div className={clsx('bg-dark-elevated rounded-lg p-3 flex-shrink-0', isVenueInactive && 'opacity-50')}>
              <div className="flex items-start gap-3 mb-2">
                <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <h3
                        className={clsx(
                          'text-sm font-semibold truncate',
                          isVenueInactive ? 'line-through text-dark-text-muted' : 'text-dark-text-primary',
                        )}
                      >
                        {localVenue.name}
                      </h3>
                      {isSyncingAll && (
                        <span title="Syncing contacts...">
                          <RefreshCw className="w-3 h-3 text-primary-400 animate-spin flex-shrink-0" />
                        </span>
                      )}
                      {venueWebsiteUrl && (
                        <a
                          href={venueWebsiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-0.5 text-xs text-primary-400 hover:text-primary-300 flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-3 h-3" />
                          Website
                        </a>
                      )}
                      {/* Is this place still going? A festival is "fini", everything else
                          "fermé"; a city gets no cross at all. Then the venue itself, with
                          "concerts" appended for a name that says nothing about music.
                          See utils/venueWebSearch.ts. */}
                      <div className="flex items-center flex-shrink-0">
                        <GoogleSearchLink
                          query={venueClosedQuery(localVenue)}
                          icon={X}
                          className="hover:bg-dark-border"
                        />
                        <GoogleSearchLink
                          query={venueGeneralQuery(localVenue)}
                          icon={Search}
                          className="hover:bg-dark-border"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {onEditVenue && (
                        <button
                          onClick={() => {
                            onEditVenue(localVenue);
                          }}
                          className="p-1 text-dark-text-muted hover:text-primary-400 hover:bg-dark-border rounded-sm transition-colors flex-shrink-0"
                          title="Edit venue"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  {isVenueInactive && (
                    <p className="text-xs text-dark-text-muted">
                      {localVenue.status === 'closed' ? 'Closed' : 'No Concerts'}
                    </p>
                  )}
                  {localVenue.address && (
                    <p className="text-xs text-dark-text-secondary truncate">{formatAddress(localVenue.address, 'short')}</p>
                  )}
                </div>
              </div>

              {/* Rendered unconditionally: the map is always shown, greyed out when unlocated */}
              <div className="mt-2 pt-2 border-t border-dark-border flex gap-3">
                {/* Text sub-columns */}
                <div className="flex-1 min-w-0 flex gap-3">
                  {/* Left column: type, style, dates. Every line is rendered whether or not the
                      venue carries the value — an empty one reads "Not set" and is the handle
                      for filling it in — and each one is right-clickable, opening the Venues
                      page's VenueFieldEditor on that single field. */}
                  <div className="flex flex-col gap-0.5 min-w-0 flex-shrink-0 max-w-[45%]">
                    {(() => {
                      const Icon = (localVenue.type && VENUE_TYPE_ICONS[localVenue.type]) || HelpCircle;
                      return (
                        <div {...venueFieldRow('type', 'the venue type')}>
                          <Icon size={14} className="text-dark-text-muted flex-shrink-0" />
                          {localVenue.type ? (
                            <span className="text-sm text-dark-text-secondary">{localVenue.type.replace('_', ' ')}</span>
                          ) : (
                            <span className="text-sm text-dark-text-muted italic">No type</span>
                          )}
                        </div>
                      );
                    })()}
                    <div {...venueFieldRow('styles', 'the music styles', 'items-start')}>
                      <Music className="w-3.5 h-3.5 text-dark-text-muted flex-shrink-0 mt-0.5" />
                      {localVenue.styles && localVenue.styles.length > 0 ? (
                        <span className="text-sm text-dark-text-secondary break-words">{localVenue.styles.join(', ')}</span>
                      ) : (
                        <span className="text-sm text-dark-text-muted italic">No styles</span>
                      )}
                    </div>
                    <MonthPeriodLine
                      label="Contact"
                      period={localVenue.ideal_contact_period}
                      rowProps={venueFieldRow('ideal_contact_period', 'the ideal contact period')}
                    />
                    <MonthPeriodLine
                      label="Answer by"
                      period={localVenue.expect_answer_by}
                      rowProps={venueFieldRow('expect_answer_by', 'when to expect an answer')}
                    />
                    <MonthPeriodLine
                      label="Concert"
                      period={localVenue.concert_period}
                      rowProps={venueFieldRow('concert_period', 'the concert period')}
                    />
                  </div>
                  {/* Vertical separator — kept whether or not there is a commentary, so the
                      commentary column always reads as its own (right-clickable) section */}
                  <div className="w-px bg-dark-border flex-shrink-0 self-stretch" />
                  {/* Right column: commentary. Right-click edits it, as its cell does on the
                      Venues page (one VenueFieldEditor on the `commentary` field). */}
                  <div
                    className="min-w-0 flex-1 cursor-context-menu rounded-sm hover:bg-dark-border/40"
                    title="Right-click to edit the commentary"
                    onContextMenu={openVenueFieldEditor('commentary')}
                  >
                    {localVenue.commentary ? (
                      <p className="text-sm text-dark-text-secondary">{localVenue.commentary}</p>
                    ) : (
                      <p className="text-sm text-dark-text-muted italic">No commentary</p>
                    )}
                  </div>
                </div>
                <div className="w-px bg-dark-border flex-shrink-0 self-stretch" />
                {/* Right sub-column: location */}
                <VenueMiniMap
                  latitude={localVenue.coordinates?.latitude}
                  longitude={localVenue.coordinates?.longitude}
                  country={localVenue.address?.country}
                  label={formatAddress(localVenue.address, 'short') || localVenue.name}
                  size={128}
                />
              </div>
            </div>
          )}

          {/* Favorites + Interest Editor */}
          {localVenue && bandsList.length > 0 && !historyExpanded && (
            <div className="bg-dark-elevated rounded-lg p-2 flex-shrink-0">
              <div className="flex items-center gap-2 mb-1.5">
                <Star className="w-3.5 h-3.5 text-dark-text-muted" />
                <h4 className="text-xs font-semibold text-dark-text-primary">Favorites</h4>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                {bandsList.map(band => {
                  const isFavorited = (localVenue.favorite_band_ids || []).includes(band._id);
                  return (
                    <button
                      key={band._id}
                      onClick={() => handleToggleFavorite(band._id)}
                      className={clsx(
                        'px-2 py-0.5 rounded-sm text-xs transition-colors',
                        isFavorited
                          ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'
                          : 'bg-dark-bg-tertiary text-dark-text-muted hover:bg-dark-bg-secondary hover:text-dark-text-secondary'
                      )}
                      title={band.name}
                    >
                      {band.name}
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mb-1.5">
                <ThumbsUp className="w-3.5 h-3.5 text-dark-text-muted" />
                <h4 className="text-xs font-semibold text-dark-text-primary">Interest</h4>
              </div>
              <div className="flex flex-wrap gap-1">
                {/* "All" button for overall interest */}
                {(() => {
                  const interest = getInterestForBand(null);
                  const cfg = getInterestConfig(interest.level);
                  return (
                    <button
                      key="all"
                      onClick={() => handleOpenInterestModal(null, 'All Bands')}
                      className={clsx(
                        'px-2 py-0.5 rounded-sm text-xs font-medium border transition-colors hover:opacity-80',
                        interest.level === 'no_information'
                          ? 'bg-dark-elevated border-dark-border text-dark-text-muted'
                          : `${cfg.bgClass} ${cfg.color}`
                      )}
                      title={interest.comment || undefined}
                    >
                      All{interest.level !== 'no_information' ? `: ${cfg.label}` : ''}
                    </button>
                  );
                })()}
                {/* Per-band buttons */}
                {bandsList.map(band => {
                  const interest = getInterestForBand(band._id);
                  const cfg = getInterestConfig(interest.level);
                  return (
                    <button
                      key={band._id}
                      onClick={() => handleOpenInterestModal(band._id, band.name)}
                      className={clsx(
                        'px-2 py-0.5 rounded-sm text-xs font-medium border transition-colors hover:opacity-80',
                        interest.level === 'no_information'
                          ? 'bg-dark-elevated border-dark-border text-dark-text-muted'
                          : `${cfg.bgClass} ${cfg.color}`
                      )}
                      title={interest.comment || undefined}
                    >
                      {band.name}{interest.level !== 'no_information' ? `: ${cfg.label}` : ''}
                    </button>
                  );
                })}
              </div>
              {/* The pause, under the badge that carries it: "Not Now" is its only indicator, and
                  this is the date it runs to. Greyed once past — nothing is rewritten when a pause
                  expires, so the venue is simply contactable again. See docs/VENUE_PAUSE.md. */}
              {(() => {
                const after = recontactDate(localVenue);
                if (!after || getInterestForBand(null).level !== PAUSE_LEVEL) return null;
                const paused = isPaused(localVenue);
                return (
                  <div className={clsx('text-xs mt-1.5', paused ? 'text-orange-400' : 'text-dark-text-muted')}>
                    <Clock className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                    {paused ? 'Do not recontact before ' : 'Was paused until '}
                    {format(after, 'MMM d, yyyy')}
                    {localVenue?.recontact?.reason && (
                      <div className="text-dark-text-muted italic">{localVenue.recontact.reason}</div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Venue Todos */}
          {localVenue && !historyExpanded && (
            <VenueTodos
              venueId={localVenue._id}
              venueName={localVenue.name}
              contacts={contacts}
              bands={bandsList}
              selectedContactId={selectedContact?._id}
              collapsed={todosCollapsed}
              onCollapsedChange={setTodosCollapsed}
            />
          )}

          {/* Communication History */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex items-center justify-between mb-2 flex-shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setHistoryExpanded(v => !v)}
                  className="flex items-center gap-1 text-sm font-semibold text-dark-text-primary hover:text-primary-400 transition-colors"
                  title={historyExpanded ? 'Show venue details (p)' : 'Hide venue details to expand communications (p)'}
                >
                  {historyExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
                  Previous Communications
                </button>
                <div className="flex items-center gap-1.5 text-xs text-dark-text-muted">
                  <button
                    type="button"
                    onClick={() => toggleDirectionFilter('sent')}
                    aria-pressed={directionFilter === 'sent'}
                    title={directionFilter === 'sent' ? 'Show every communication (s)' : 'Show only sent (s)'}
                    className={clsx(
                      'flex items-center gap-1 rounded-sm border px-1.5 py-0.5 transition-colors',
                      directionFilter === 'sent'
                        ? 'border-primary-500 bg-primary-500/15'
                        : 'border-transparent hover:bg-dark-elevated',
                    )}
                  >
                    <ArrowUpRight className="w-3 h-3 text-primary-400" />
                    <span className="text-primary-400 font-medium">{stats.totalSent}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleDirectionFilter('received')}
                    aria-pressed={directionFilter === 'received'}
                    title={directionFilter === 'received' ? 'Show every communication (r)' : 'Show only received (r)'}
                    className={clsx(
                      'flex items-center gap-1 rounded-sm border px-1.5 py-0.5 transition-colors',
                      directionFilter === 'received'
                        ? 'border-green-500 bg-green-500/15'
                        : 'border-transparent hover:bg-dark-elevated',
                    )}
                  >
                    <ArrowDownLeft className="w-3 h-3 text-green-400" />
                    <span className="text-green-400 font-medium">{stats.totalReceived}</span>
                  </button>
                  <span
                    className="flex items-center gap-1"
                    title={stats.lastContactNote ? `Last contact — ${stats.lastContactNote}` : 'Last contact'}
                  >
                    <Clock className="w-3 h-3" />
                    <span>{stats.lastContact ? getTimeAgo(stats.lastContact) : 'Never'}</span>
                  </span>
                </div>
              </div>
              <select
                value={historyFilter}
                onChange={(e) => setHistoryFilter(e.target.value)}
                className="text-xs bg-dark-elevated border border-dark-border rounded-sm px-2 py-1 text-dark-text-primary"
              >
                <option value="all">All Contacts</option>
                {contacts.map(c => (
                  <option key={c._id} value={c._id}>{contactDisplayName(c)}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto space-y-2">
              {allCommunications.length === 0 ? (
                <div className="text-center py-4 text-dark-text-muted text-xs">
                  {directionFilter === 'sent' ? 'No sent communications'
                    : directionFilter === 'received' ? 'No received communications'
                    : 'No previous communications'}
                </div>
              ) : (
                allCommunications.map((entry, i) => {
                  const commKey = `${entry.contact._id}-${entry.index}-${entry.threadMessage?.gmail_message_id ?? ''}-${i}`;
                  const isThreadEntry = !!entry.threadMessage && !!entry.comm.thread;
                  const isSingleEmail = entry.comm.type === 'email' && !!entry.comm.email;
                  const isSelectedForReply = isThreadEntry
                    ? replyingTo?.threadId === entry.comm.thread?.gmail_thread_id && replyingTo?.messageId === entry.threadMessage?.gmail_message_id
                    : isSingleEmail && !replyingTo?.threadId && replyingTo?.messageId === (entry.comm.email?.gmail_message_id || '') && replyingTo?.subject === entry.comm.email?.subject;

                  return (
                    <CommunicationCard
                      key={commKey}
                      commKey={commKey}
                      entry={entry}
                      isExpanded={expandedComms.has(commKey)}
                      isSelectedForReply={isSelectedForReply}
                      showContactName={true}
                      onToggleExpand={handleToggleExpand}
                      onDeleteConfirm={handleDeleteConfirm}
                      onReschedule={handleRescheduleOpen}
                      onSendNow={handleSendNowOpen}
                      onThreadClick={handleThreadClick}
                      onMarkReadToggle={handleMarkReadToggle}
                      onEmailContextMenu={handleEmailContextMenu}
                      bandMap={bandMap}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Contact Selection & New Communication */}
        <div ref={rightPanelRef} className="w-1/2 flex flex-col gap-4 overflow-y-auto">
          {/* Section 1: Select Contact */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-shrink-0">
              <h4 className="text-sm font-semibold text-dark-text-primary">Select Contact ({contacts.length})</h4>
              {/* Hunt for a contact that is not on file yet: a Google search for the venue's
                  booking address — the mairie's cultural department for a city, a programmer for
                  anything else. See utils/venueWebSearch.ts. */}
              {localVenue && (
                <GoogleSearchLink
                  query={venueSearchQuery(localVenue)}
                  icon={Search}
                  className="hover:bg-dark-elevated"
                />
              )}
            </div>

            {/* Contact Selector with New Contact button */}
            <div className="flex gap-2 flex-shrink-0 items-center">
              <select
                value={selectedContact?._id ?? ''}
                onChange={(e) => { setSelectedContactId(e.target.value); setReplyingTo(null); }}
                className="flex-1 text-sm bg-dark-elevated border border-dark-border rounded-lg px-3 py-1.5 text-dark-text-primary focus:outline-hidden focus:ring-2 focus:ring-primary-500"
              >
                {contacts.map(contact => (
                  <option key={contact._id} value={contact._id}>{contactDisplayName(contact)}</option>
                ))}
              </select>
              <Button
                size="sm"
                variant={showNewContactForm ? 'primary' : 'ghost'}
                onClick={() => { setNewContactPrefill(null); setShowNewContactForm(!showNewContactForm); }}
              >
                <UserPlus className="w-3 h-3" />
                {showNewContactForm ? 'Cancel' : 'New'}
              </Button>
            </div>

            {/* New Contact Form */}
            {showNewContactForm && (
              <div ref={newContactFormRef} className="bg-dark-elevated rounded-lg p-3 flex-shrink-0 border border-primary-700/50">
                <h5 className="text-xs font-semibold text-dark-text-primary flex items-center gap-2 mb-3">
                  <UserPlus className="w-3 h-3" />
                  Add New Contact
                </h5>
                <ContactForm
                  mode="create"
                  initialData={newContactPrefill ?? undefined}
                  onSubmit={handleCreateNewContact}
                  onCancel={() => { setShowNewContactForm(false); setNewContactPrefill(null); }}
                  error={newContactError}
                  venueScoped
                />
              </div>
            )}

            {/* Selected Contact Info - only show if there are contacts */}
            {hasContacts && selectedContact && (
              <>
                {isEditingContact ? (
                  <div className="bg-dark-elevated rounded-lg p-3 flex-shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="text-xs font-semibold text-dark-text-primary">Edit Contact</h5>
                      <button
                        onClick={() => setIsEditingContact(false)}
                        className="p-1 text-dark-text-muted hover:text-dark-text-primary rounded-sm"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <ContactForm
                      mode="edit"
                      contact={selectedContactAtVenue}
                      onSubmit={handleSaveContact}
                      onCancel={() => setIsEditingContact(false)}
                      error={editContactError}
                      venueScoped={Boolean(localVenue)}
                    />
                  </div>
                ) : (
                  <div className="bg-dark-elevated rounded-lg p-2 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-dark-text-primary truncate">
                          {contactDisplayName(selectedContact)}
                          {selectedContact.function && (
                            <span className="text-dark-text-muted font-normal"> ({selectedContact.function})</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-dark-text-secondary">
                          {selectedContact.email && (
                            <button
                              type="button"
                              className="flex items-center gap-1 truncate hover:text-primary-400 transition-colors cursor-pointer"
                              title="Click to copy email"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(selectedContact!.email!);
                                setCopiedEmail(true);
                                setTimeout(() => setCopiedEmail(false), 1500);
                              }}
                            >
                              {copiedEmail ? <Check className="w-3 h-3 text-green-400" /> : <MailIcon className="w-3 h-3" />}
                              <span className={copiedEmail ? 'text-green-400' : ''}>{copiedEmail ? 'Copied!' : selectedContact.email}</span>
                            </button>
                          )}
                          {selectedContact.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {selectedContact.phone}
                            </span>
                          )}
                        </div>
                      </div>
                      {contactFlag !== 'none' && (
                        <Badge variant={contactFlag === 'gatekeeper' ? 'info' : 'warning'} className="text-xs">{contactFlag.replace(/_/g, ' ')}</Badge>
                      )}
                      <div className="flex gap-1 flex-shrink-0">
                        <button
                          onClick={
                            gmailGranted && selectedContact.email && syncingContactId !== selectedContact._id && !isSyncingAll
                              ? () => handleSyncContact(selectedContact._id)
                              : undefined
                          }
                          disabled={!gmailGranted || !selectedContact.email || syncingContactId === selectedContact._id || isSyncingAll}
                          className={clsx(
                            'p-1.5 rounded-sm transition-colors',
                            !gmailGranted || !selectedContact.email
                              ? 'text-dark-text-muted opacity-40 cursor-not-allowed'
                              : syncDoneContactId === selectedContact._id
                              ? 'text-green-400'
                              : 'text-dark-text-muted hover:text-primary-400 hover:bg-dark-border',
                            (syncingContactId === selectedContact._id || isSyncingAll) && 'cursor-wait'
                          )}
                          title={
                            !gmailGranted
                              ? 'You must grant email permissions to sync emails. Go to Profile → Account → Grant Permissions'
                              : !selectedContact.email
                              ? 'No email address'
                              : 'Sync communications'
                          }
                        >
                          {syncDoneContactId === selectedContact._id ? (
                            <Check className="w-3.5 h-3.5" />
                          ) : (
                            <RefreshCw className={clsx('w-3.5 h-3.5', (syncingContactId === selectedContact._id || (isSyncingAll && !!selectedContact.email)) && 'animate-spin')} />
                          )}
                        </button>
                        <button
                          onClick={() => setMailingListPickerOpen(true)}
                          disabled={!selectedContact.email}
                          className={clsx(
                            'p-1.5 rounded-sm transition-colors',
                            selectedContact.email
                              ? 'text-dark-text-muted hover:text-primary-400 hover:bg-dark-border'
                              : 'text-dark-text-muted opacity-40 cursor-not-allowed'
                          )}
                          title={selectedContact.email ? 'Add to mailing list' : 'No email address'}
                        >
                          <ListPlus className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={handleStartEditContact}
                          className="p-1.5 text-dark-text-muted hover:text-primary-400 hover:bg-dark-border rounded-sm transition-colors"
                          title="Edit contact"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeleteContactConfirm(selectedContact._id)}
                          className="p-1.5 text-dark-text-muted hover:text-red-400 hover:bg-red-900/20 rounded-sm transition-colors"
                          title="Delete contact"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {selectedContact.commentary && (
                      <p className="text-xs text-dark-text-muted mt-1.5 pl-10 line-clamp-2">{selectedContact.commentary}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Divider */}
          {hasContacts && selectedContact && !isEditingContact && (
            <div className="border-t border-dark-border" />
          )}

          {/* Section 2: New Communication (docs/CONTACT_OVERLAY_PERF.md) */}
          <CommunicationComposer
            contact={selectedContact}
            venue={localVenue}
            emailTemplates={emailTemplates}
            isEmailBlocked={isEmailBlocked}
            emailBlockedReason={emailBlockedReason}
            replyingTo={replyingTo}
            onClearReplyingTo={clearReplyingTo}
            onSaved={handleCommunicationSaved}
            onFavoritedBands={handleFavoritedBands}
            hidden={isEditingContact}
          />

          {/* No contacts state */}
          {!hasContacts && !showNewContactForm && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-dark-text-muted">
                <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="text-sm mb-2">No contacts for this venue</p>
                <p className="text-xs">Click "New" above to add one</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Send Now Confirmation Modal */}
      {sendNowConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 max-w-sm">
            <h3 className="text-sm font-semibold text-dark-text-primary mb-2">
              Send Email?
            </h3>
            <p className="text-xs text-dark-text-secondary mb-1">
              <span className="font-medium">To:</span> {sendNowConfirm.to}
            </p>
            <p className="text-xs text-dark-text-secondary mb-4">
              <span className="font-medium">Subject:</span> {sendNowConfirm.subject}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setSendNowConfirm(null)}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSendNowConfirm}>
                <Send className="w-3 h-3" />
                Send
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Send Email Confirmation Modal */}
      {/* Delete Communication Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 max-w-sm">
            <h3 className="text-sm font-semibold text-dark-text-primary mb-2">
              {deleteConfirm.scheduledEmailId ? 'Cancel Scheduled Email?' : 'Delete Communication?'}
            </h3>
            <p className="text-xs text-dark-text-secondary mb-4">
              {deleteConfirm.scheduledEmailId
                ? 'This will cancel the scheduled email. It will not be sent. Are you sure?'
                : 'The email will be deleted from this service but not from your email provider. This action cannot be undone. Are you sure you want to delete this communication?'}
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleDeleteCommunication(deleteConfirm.contactId, deleteConfirm.commIndex, deleteConfirm.gmailMessageId, deleteConfirm.scheduledEmailId)}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleteConfirm.scheduledEmailId ? 'Cancel Email' : 'Delete Communication'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setRescheduleTarget(null)}>
          <div className="bg-dark-surface border border-dark-border rounded-lg p-4 w-80" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-dark-text-primary mb-3">Reschedule Email</h3>
            <div className="py-1 mb-2">
              {schedulePresets.map((preset) => (
                <button
                  key={preset.value}
                  disabled={isRescheduling}
                  onClick={() => handleRescheduleConfirm(preset.value)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-dark-elevated transition-colors rounded-sm disabled:opacity-50"
                >
                  <span className="text-dark-text-primary">{preset.label}</span>
                  <span className="text-dark-text-tertiary">{preset.sublabel}</span>
                </button>
              ))}
            </div>
            <div className="border-t border-dark-border pt-3 space-y-2">
              <p className="text-xs font-medium text-dark-text-secondary px-1">Custom</p>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  className="flex-1 px-2 py-1.5 text-xs rounded-md bg-dark-elevated border border-dark-border text-dark-text-primary"
                />
                <input
                  type="time"
                  value={rescheduleTime}
                  onChange={(e) => setRescheduleTime(e.target.value)}
                  className="w-24 px-2 py-1.5 text-xs rounded-md bg-dark-elevated border border-dark-border text-dark-text-primary"
                />
              </div>
              <Button
                size="sm"
                variant="primary"
                className="w-full"
                disabled={!rescheduleDate || !rescheduleTime || isRescheduling}
                onClick={handleRescheduleCustom}
              >
                <Calendar className="w-3 h-3" />
                Reschedule for {rescheduleDate && rescheduleTime
                  ? format(new Date(`${rescheduleDate}T${rescheduleTime}`), 'MMM d, HH:mm')
                  : '...'}
              </Button>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" onClick={() => setRescheduleTarget(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quick editor, summoned by right-clicking one of the venue card's field lines. Above
          the overlay's own backdrop (`.modal-backdrop`, z-index 9999) since it portals straight
          to the body. The core-field gate matches the Venues page's: on a shared venue record
          only an admin can touch the fields the server keeps there. */}
      {venueFieldEditor && localVenue && (
        <VenueFieldEditor
          venue={localVenue}
          field={venueFieldEditor.field}
          position={venueFieldEditor.position}
          onSave={handleSaveVenueField}
          onClose={() => setVenueFieldEditor(null)}
          readonlyCoreFields={
            !user?.is_admin &&
            (localVenue.visibility === 'public' || localVenue.visibility === 'pending_public')
          }
          bands={bandsList}
          zIndex={10000}
        />
      )}

      {/* Interest Edit Modal */}
      {interestModalOpen && <InterestModal
        isOpen={interestModalOpen}
        onClose={() => setInterestModalOpen(false)}
        bandLabel={interestEditLabel}
        currentLevel={getInterestForBand(interestEditBandId).level}
        currentComment={getInterestForBand(interestEditBandId).comment || ''}
        isOverall={interestEditBandId === null}
        currentRecontact={localVenue?.recontact ?? null}
        onSave={handleSaveInterest}
      />}

      {/* Delete Contact Confirmation Modal */}
      {deleteContactConfirm && (() => {
        const contactToDelete = contacts.find(c => c._id === deleteContactConfirm);
        const isLastVenue = !contactToDelete || contactToDelete.venue_ids.length <= 1;
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-dark-surface border border-dark-border rounded-lg p-4 max-w-sm">
              <h3 className="text-sm font-semibold text-dark-text-primary mb-2">
                {isLastVenue ? 'Delete Contact?' : 'Remove from Venue?'}
              </h3>
              <p className="text-xs text-dark-text-secondary mb-4">
                {isLastVenue
                  ? 'This will permanently delete this contact and all their communications. This action cannot be undone.'
                  : `This contact is linked to ${contactToDelete!.venue_ids.length} venues. Removing them here will only dissociate them from this venue — their data and other venue links will be preserved.`}
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="ghost" size="sm" onClick={() => setDeleteContactConfirm(null)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleDeleteContact(deleteContactConfirm)}
                  isLoading={isDeletingContact}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isLastVenue ? 'Delete Contact' : 'Remove from Venue'}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add this contact to an existing mailing list */}
      <MailingListPickerModal
        isOpen={mailingListPickerOpen}
        onClose={() => setMailingListPickerOpen(false)}
        onPick={handleAddContactToMailingList}
        isSubmitting={isAddingToMailingList}
      />

    </Modal>
  );
}
